import { DataSource } from '@angular/cdk/collections';
import { MatPaginator, MatSort } from '@angular/material';
import { map } from 'rxjs/operators';
import { Observable, merge, BehaviorSubject } from 'rxjs';
import { isNullOrUndefined } from 'util';
import { Gate } from '../../models/gate';
import { Screen } from '../../models/screen';
import { Entrance } from '../../models/entrance';

export class GateTableData {
  gate: Gate;
  entrance: Entrance;
  screens: Screen[] = [];
}

/**
 * Data source for the TableGates view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class TableGatesDataSource extends DataSource<GateTableData> {

  // Data is behaviour subject for observing changes
  private _data = new BehaviorSubject<GateTableData[]>([]);

  get data(): GateTableData[] {
    return this._data.value;
  }

  set data(gates: GateTableData[]) {
    this._data.next(gates);
  }

  // Filter
  private _filterChange = new BehaviorSubject('');

  filteredDataLength: number = 0; // need length for paginators in table

  get filter(): string {
      return this._filterChange.value || "";
  }

  set filter(filter: string) {
      this._filterChange.next(filter);
  }

  constructor(private paginator: MatPaginator, private sort: MatSort) {
    super();
  }

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */
  connect(): Observable<GateTableData[]> {

    // Combine everything that affects the rendered data into one update
    // stream for the data-table to consume.
    const dataMutations = [
      this.paginator.page,
      this.sort.sortChange,
      this._filterChange,
      this._data
    ];

    // Set the paginators length
    this.paginator.length = this.data.length;

    return merge(...dataMutations).pipe(map(() => {
      return this.getPagedData(this.getSortedData(this.getFilteredData([...this.data])))
    }));
  }

  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */
  disconnect() {}

  // Iterate over every field in the TableGatesItem class,
  // checking for matches with the filter string.
  // Ignores the field names, and only uses the field value (val)
  private filterFunction(item: GateTableData) {
    if(isNullOrUndefined(item)) {
      return false;
    }
    
    // Check gate
    if(!isNullOrUndefined(item.gate) && Object.entries(item.gate).some(([,val]) => {
      // Ignore boolean types because no one wants to search for "true"/"false"
      if(!isNullOrUndefined(val) && typeof val != "boolean") {
        let valString: string = val.toString().toLowerCase().trim();
        if (valString.indexOf(this.filter) != -1) {
          // Uncomment for debug output
          // console.log("Filter value=" + valString + ", type=" + typeof val);
          return true;
        }
      }
    })) {
      // Match! One of the fields of the class matched the filter string
      return true;
    }

    // Check entrance
    if(!isNullOrUndefined(item.entrance) && Object.entries(item.entrance).some(([,val]) => {
      // Ignore boolean types because no one wants to search for "true"/"false"
      if(!isNullOrUndefined(val) && typeof val != "boolean") {
        let valString: string = val.toString().toLowerCase().trim();
        if (valString.indexOf(this.filter) != -1) {
          // Uncomment for debug output
          // console.log("Filter value=" + valString + ", type=" + typeof val);
          return true;
        }
      }
    })) {
      // Match! One of the fields of the class matched the filter string
      return true;
    }
    // No match
    return false;
  }

  private getFilteredData(data: GateTableData[]) {
    let filteredData = data.filter(this.filterFunction, this);
    this.filteredDataLength = filteredData.length; // need length for paginators in table
    return filteredData;
  }

  /**
   * Paginate the data (client-side). If you're using server-side pagination,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getPagedData(data: GateTableData[]) {
    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    return data.splice(startIndex, this.paginator.pageSize);
  }

  /**
   * Sort the data (client-side). If you're using server-side sorting,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getSortedData(data: GateTableData[]) {
    if (!this.sort.active || this.sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const isAsc = this.sort.direction === 'asc';
      switch (this.sort.active) {
        // All Strings for now, change these if they change to numbers
        // case 'version': return compare(a.version, b.version, isAsc);
        // case 'uptime': return compare(a.uptime, b.uptime, isAsc);

        case 'gateserialNumber': return compare(a.gate.serialNumber, b.gate.serialNumber, isAsc);
        case 'gatename': return compare(a.gate.name, b.gate.name, isAsc);
        case 'gatedescription': return compare(a.gate.description, b.gate.description, isAsc);
        case 'entrancename': return compare(a.entrance.name, b.entrance.name, isAsc);

        // // String types
        // case 'gateName': return compare(a.gateName, b.gateName, isAsc);
        // case 'gateLocation': return compare(a.gateLocation, b.gateLocation, isAsc);
        // case 'gateStore': return compare(a.gateStore, b.gateStore, isAsc);
        // case 'gateGroup': return compare(a.gateGroup, b.gateGroup, isAsc);

        // // Example of a number (notice the + in front of a and b)
        // case 'cpuTempCelsius': return compare(+a.cpuTempFahrenheit, +b.cpuTempFahrenheit, isAsc);
        
        default: return 0;
      }
    });
  }
}

/** Simple sort comparator for example ID/Name columns (for client-side sorting). */
function compare(a, b, isAsc) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}


